// Third-Party
import { Paper, Table, TableBody, TableContainer } from "@mui/material";
import { SelectChangeEvent } from "@mui/material/Select";

// Internal
import useLocales from "@hooks/useLocales";
import generate from "@utils/generateElem";
import MyTablePagination from "src/components/pagination/TablePagination";
import Scrollbar from "src/components/Scrollbar";
import { TableNoData, TableSkeleton } from "src/components/table";
import TableHeadCustom from "src/pages/Documents/Finance/table/tableHeadCustom";

// Types
import { IPurchaseRequisition } from "../../types/purchaseRequisitionTypes";
import { ProcurementTableRow } from "./ProcurementTableRow";

interface ProcurementTableProps {
  rows: IPurchaseRequisition[];
  loading: boolean;
  page: number;
  rowsPerPage: number;
  hasNextPage: boolean;
  onPageChange: (event: any, newPage: number) => void;
  onRowsPerPageChange: (event: SelectChangeEvent<HTMLInputElement>) => void;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSortChange?: (field: string, order: "asc" | "desc") => void;
  onDelete: (documentId: string) => Promise<void>;
  onPost: (documentId: string) => Promise<void>;
  isDraft?: boolean;
}

const ProcurementTable: React.FC<ProcurementTableProps> = ({
  rows,
  loading,
  page,
  rowsPerPage,
  hasNextPage,
  onPageChange,
  onRowsPerPageChange,
  sortBy,
  sortOrder,
  onSortChange,
  onDelete,
  onPost,
  isDraft = false,
}) => {
  const { translate } = useLocales();
  const isNotFound = !loading && rows.length === 0;

  const TABLE_HEAD = [
    ...(!isDraft
      ? [
          {
            id: "documentNumber",
            label: "purchase_requisition_number",
            align: "left",
          },
        ]
      : []),
    { id: "requesterName", label: "requester", align: "left" },
    ...(!isDraft
      ? [
          {
            id: "status",
            label: "review_screen.status",
            align: "left",
          },
        ]
      : []),
    { id: "vendorName", label: "vendor_name", align: "left" },
    { id: "department", label: "department", align: "left" },
    {
      id: "createdDate",
      label: "created_date",
      align: "left",
      sortable: true,
    },
    {
      id: "dueDeliveryDate",
      label: "due_delivery_date",
      sortable: true,
      align: "left",
    },
    { id: "actions", label: "actions", align: "right" },
  ];

  const handleSort = (id: string) => {
    if (!onSortChange) return;
    if (sortBy === id) {
      onSortChange(id, sortOrder === "asc" ? "desc" : "asc");
    } else {
      onSortChange(id, "asc");
    }
  };

  return (
    <Paper>
      <Scrollbar>
        <TableContainer sx={{ minWidth: 800 }}>
          <Table size="small">
            <TableHeadCustom
              headLabel={TABLE_HEAD.map((head) => ({
                ...head,
                label: translate(head.label),
              }))}
              onSort={handleSort}
            />
            <TableBody>
              {loading ? (
                generate(<TableSkeleton />)
              ) : isNotFound ? (
                <TableNoData isNotFound={isNotFound} />
              ) : (
                rows.map((row) => (
                  <ProcurementTableRow
                    key={row.id}
                    row={row}
                    isDraft={isDraft}
                    onDelete={() => onDelete(row.id)}
                    onPost={() => onPost(row.id)}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Scrollbar>

      <MyTablePagination
        page={page - 1}
        count={rows.length}
        rowsPerPage={rowsPerPage}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
        hasNextPage={hasNextPage}
        hidePageNo={true}
        loading={loading}
        dataLength={rows.length}
      />
    </Paper>
  );
};

export default ProcurementTable;
